/**
 * main - Application Entry Point
 * MODULE: _shared/frontend-shell/src/main.tsx
 * PURPOSE: Initialize React application with providers and routing
 * DEPENDENCIES: react, react-dom, @tanstack/react-query, react-router-dom, ./config, ./dev-tools/insightsDevHelper, ./App, ./index.css
 * SIDE EFFECTS: React rendering, DOM manipulation, QueryClient initialization, ./config prod validation
 * AGENT SAFETY: 🔴 PROTECTED - Changes affect app initialization
 * RECENT: 2026-08-06 [2.2.2130] — ACA sprint close-out; installReloadOnStaleChunk retained
 * RECENT_PRIOR: 2026-08-05 [2.2.2128] — installReloadOnStaleChunk (post-deploy hashed asset 404 recovery).
 * RECENT_PRIOR: 2026-05-09 — **[2.2.894]** dev-only **`./dev-tools/insightsDevHelper`**. 2026-03-26 [2.2.197] - import ./config before React.
 *
 * Agent Notes:
 * - DO NOT modify QueryClient configuration without approval
 * - DO NOT remove React.StrictMode
 * - DO NOT change provider order
 * - Keep file under 50 lines
 */

import './config';
import './dev-tools/insightsDevHelper';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { installReloadOnStaleChunk } from './lib/reloadOnStaleChunk';
import './index.css';

installReloadOnStaleChunk();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter 
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
